import { AfterViewInit, Component, Input, TemplateRef, ViewChild } from "@angular/core";

@Component({
    selector: "partywiseAssignStock",
    templateUrl: "./partywiseAssignStock.html",
})
export class partywiseAssignStock implements AfterViewInit {
    @ViewChild('exactTab') exactTab: TemplateRef<any>;
    @ViewChild('mismatchTab') mismatchTab: TemplateRef<any>;
    @ViewChild('sizeTab') sizeTab: TemplateRef<any>;

    activeTab: string = 'exact'; // default

    @Input() pendingOrderData = [];
    @Input() pendingOrderSetsData = [];
    @Input() pendingOrderMismatchSetsData = [];
    @Input() pendingOrderSizesData = [];
    @Input() stocks = {};
    @Input() setAndSizeList = {};
    @Input() uniqueStockSets = [];
    @Input() type = '';
    @Input() summary = {
        pieces: {
            fs: 0,
            hs: 0,
            total: 0,
            totalSets: 0,
            totalSizes: 0
        },
        exact: {
            stock: { total: 0 },
            assigned: { fs: 0, hs: 0, total: 0 }
        },
        mismatch: {
            stock: { total: 0 },
            assigned: { fs: 0, hs: 0, total: 0 }
        },
        size: {
            stock: { total: 0 },
            assigned: { fs: 0, hs: 0, total: 0 }
        }
    }

    isViewInitCompleted = false;

    get isPartyWise() {
        return this.type === 'Party Wise';
    }

    ngOnInit() {
        this.initializeAssignedObjects();
    }

    ngAfterViewInit(): void {
        this.isViewInitCompleted = true;
    }

    ngOnChanges() {
        this.initializeAssignedObjects();
        if (this.isPartyWise && this.pendingOrderData.length > 0) {
            this.setDefaultAssignedQuantities();
        }
        this.calculateAllSummaries();
    }

    // Initialize assigned objects for all rows
    private initializeAssignedObjects() {
        this.pendingOrderData.forEach(row => {
            if (!row.assigned) {
                row.assigned = {};
            }
            ['exact', 'mismatch', 'size'].forEach(tab => {
                if (!row.assigned[tab]) {
                    row.assigned[tab] = { total: 0 };
                }
            });
        });
    }

    // Set default assigned quantities based on pending quantities (only for Party Wise)
    private setDefaultAssignedQuantities() {
        if (!this.isPartyWise) return;

        this.pendingOrderData.forEach(row => {
            const stockObj = this.stocks[row.design_id];
            if (!stockObj) return;

            // Handle SET assignments for exact tab
            if (row.sets && Object.keys(row.sets).length > 0) {
                Object.keys(row.sets).forEach(setKey => {
                    const pendingSet = row.sets[setKey];
                    const stockSet = stockObj.sets && stockObj.sets[setKey] ? stockObj.sets[setKey] : null;
                    
                    if (pendingSet && stockSet && pendingSet.qty && stockSet.stock > 0) {
                        const assignableQty = Math.min(pendingSet.qty, stockSet.stock - stockSet.assigned);
                        if (assignableQty > 0) {
                            // Set for exact tab
                            row.assigned.exact[setKey] = assignableQty;
                            stockSet.assigned += assignableQty;
                            
                            // Update pieces summary
                            const pieces = Number(pendingSet.pieces || 0);
                            const sleeveType = row.sleeve_type === 'F/S' ? 'fs' : 'hs';
                            this.summary.pieces[sleeveType] += assignableQty * pieces;
                        }
                    }
                });
                
                // Update exact tab total for this row
                this.updateRowTotal('exact', row);
            }

            // Handle SIZE assignments for size tab
            if (row.sizes && Object.keys(row.sizes).length > 0) {
                Object.keys(row.sizes).forEach(sizeKey => {
                    const pendingSize = row.sizes[sizeKey];
                    const stockSize = stockObj.sizes && stockObj.sizes[sizeKey] ? stockObj.sizes[sizeKey] : null;
                    
                    if (pendingSize && stockSize && pendingSize.qty && stockSize.stock > 0) {
                        const assignableQty = Math.min(pendingSize.qty, stockSize.stock - stockSize.assigned);
                        if (assignableQty > 0) {
                            // Set for size tab
                            row.assigned.size[sizeKey] = assignableQty;
                            stockSize.assigned += assignableQty;
                            
                            // Update pieces summary
                            const sleeveType = row.sleeve_type === 'F/S' ? 'fs' : 'hs';
                            this.summary.pieces[sleeveType] += assignableQty;
                        }
                    }
                });
                
                // Update size tab total for this row
                this.updateRowTotal('size', row);
            }
        });

        console.log("=== DEFAULT ASSIGNMENTS SET ===");
        console.log("Updated pendingOrderData with default assignments:", this.pendingOrderData);
    }

    getTabTemplate(tab: string): TemplateRef<any> {
        switch (tab) {
            case 'exact': return this.exactTab;
            case 'mismatch': return this.mismatchTab;
            case 'size': return this.sizeTab;
            default: return this.exactTab;
        }
    }

    // Set functions
    getSetPendingQty(tab: string, row: any, set: string): number {
        const key = set + '_' + row.sleeve_type;
        const pending = row.sets && row.sets[key] && row.sets[key].qty ? row.sets[key].qty : 0;

        if (tab === 'exact') {
            return pending;
        } else if (tab === 'mismatch') {
            return pending - (row.assigned.exact[key] || 0);
        }
        return pending;
    }

    getSetStockQty(tab: string, row: any, set: string): number {
        const key = set + '_' + row.sleeve_type;
        const stockObj = this.stocks && 
            this.stocks[row.design_id] && 
            this.stocks[row.design_id].sets && 
            this.stocks[row.design_id].sets[key] ? this.stocks[row.design_id].sets[key] : null;

        if (!stockObj) return 0;

        const availableStock = stockObj.stock - stockObj.assigned;
        const currentAssigned = this.getSetAssignedQty(tab, row, set);
        
        return Math.max(0, availableStock + currentAssigned);
    }

    getSetAssignedQty(tab: string, row: any, set: string): number {
        const key = set + '_' + row.sleeve_type;
        return Number(row.assigned && row.assigned[tab] && row.assigned[tab][key] ? row.assigned[tab][key] : 0);
    }

    onSetInputFocus(event: any) {
        // Select all text when input gets focus
        event.target.select();
    }

    onSetInputChange(event: any, tab: string, row: any, set: string) {
        if (!this.isViewInitCompleted) return;

        const key = set + '_' + row.sleeve_type;
        const newValue = Number(event.target.value) || 0;
        const stockObj = this.stocks && 
            this.stocks[row.design_id] && 
            this.stocks[row.design_id].sets && 
            this.stocks[row.design_id].sets[key] ? this.stocks[row.design_id].sets[key] : null;

        if (!stockObj) {
            event.target.value = 0;
            return;
        }

        // Get old value for calculation
        const oldAssigned = this.getSetAssignedQty(tab, row, set);

        // Validate against available stock
        const maxAllowed = stockObj.stock - stockObj.assigned + oldAssigned;
        const finalValue = Math.min(Math.max(0, newValue), maxAllowed);

        // Update the model
        if (!row.assigned[tab]) row.assigned[tab] = { total: 0 };
        row.assigned[tab][key] = finalValue;
        event.target.value = finalValue;

        // Update stock assigned
        stockObj.assigned += (finalValue - oldAssigned);

        // Update row total
        this.updateRowTotal(tab, row);

        // Update pieces summary
        const pieces = Number(stockObj.pieces);
        const sleeveType = row.sleeve_type === 'F/S' ? 'fs' : 'hs';
        this.summary.pieces[sleeveType] += (finalValue - oldAssigned) * pieces;

        // Recalculate summaries
        this.calculateAllSummaries();
    }

    // Size functions
    getSizePendingQty(row: any, size: string): number {
        const key = size + '_' + row.sleeve_type;
        return row.sizes && row.sizes[key] && row.sizes[key].qty ? row.sizes[key].qty : 0;
    }

    getSizeStockQty(row: any, size: string): number {
        const key = size + '_' + row.sleeve_type;
        const stockObj = this.stocks && 
            this.stocks[row.design_id] && 
            this.stocks[row.design_id].sizes && 
            this.stocks[row.design_id].sizes[key] ? this.stocks[row.design_id].sizes[key] : null;

        if (!stockObj) return 0;

        const availableStock = stockObj.stock - stockObj.assigned;
        const currentAssigned = this.getSizeAssignedQty(row, size);
        
        return Math.max(0, availableStock + currentAssigned);
    }

    getSizeAssignedQty(row: any, size: string): number {
        const key = size + '_' + row.sleeve_type;
        return Number(row.assigned && row.assigned['size'] && row.assigned['size'][key] ? row.assigned['size'][key] : 0);
    }

    onSizeInputFocus(event: any) {
        // Select all text when input gets focus
        event.target.select();
    }

    onSizeInputChange(event: any, row: any, size: string) {
        if (!this.isViewInitCompleted) return;

        const key = size + '_' + row.sleeve_type;
        const newValue = Number(event.target.value) || 0;
        const stockObj = this.stocks && 
            this.stocks[row.design_id] && 
            this.stocks[row.design_id].sizes && 
            this.stocks[row.design_id].sizes[key] ? this.stocks[row.design_id].sizes[key] : null;

        if (!stockObj) {
            event.target.value = 0;
            return;
        }

        // Get old value for calculation
        const oldAssigned = this.getSizeAssignedQty(row, size);

        // Validate against available stock
        const maxAllowed = stockObj.stock - stockObj.assigned + oldAssigned;
        const finalValue = Math.min(Math.max(0, newValue), maxAllowed);

        // Update the model
        if (!row.assigned.size) row.assigned.size = { total: 0 };
        row.assigned.size[key] = finalValue;
        event.target.value = finalValue;

        // Update stock assigned
        stockObj.assigned += (finalValue - oldAssigned);

        // Update row total
        this.updateRowTotal('size', row);

        // Update pieces summary
        const sleeveType = row.sleeve_type === 'F/S' ? 'fs' : 'hs';
        this.summary.pieces[sleeveType] += (finalValue - oldAssigned);

        // Recalculate summaries
        this.calculateAllSummaries();
    }

    private updateRowTotal(tab: string, row: any) {
        if (!row.assigned[tab]) return;

        let total = 0;
        Object.keys(row.assigned[tab]).forEach(key => {
            if (key !== 'total') {
                total += Number(row.assigned[tab][key] || 0);
            }
        });
        row.assigned[tab].total = total;
    }

    private calculateAllSummaries() {
        // Reset summaries
        this.summary.pieces.total = this.summary.pieces.fs + this.summary.pieces.hs;

        ['exact', 'mismatch', 'size'].forEach(tab => {
            this.calculateTabSummary(tab);
        });

        // Update totals
        this.summary.pieces.totalSets = this.summary.exact.assigned.total + this.summary.mismatch.assigned.total;
        this.summary.pieces.totalSizes = this.summary.size.assigned.total;
    }

    private calculateTabSummary(tab: string) {
        const summary = this.summary[tab];
        if (!summary) return;

        let assignedFs = 0;
        let assignedHs = 0;

        this.pendingOrderData.forEach(row => {
            const tabObj = row.assigned && row.assigned[tab] ? row.assigned[tab] : null;
            if (tabObj && tabObj.total) {
                if (row.sleeve_type === 'F/S') {
                    assignedFs += tabObj.total;
                } else {
                    assignedHs += tabObj.total;
                }
            }
        });

        summary.assigned = {
            fs: assignedFs,
            hs: assignedHs,
            total: assignedFs + assignedHs
        };
    }

    getSummary() {
        return this.summary[this.activeTab];
    }

    isActiveTabRowHavePendingQty(tab: string, row: any): boolean {
        if (tab === 'size') {
            return Boolean(row.sizes && Object.keys(row.sizes).length);
        } else {
            return Boolean(row.sets && Object.keys(row.sets).length);
        }
    }

    click(row: any) {
        console.log("row :: ", row);
        console.log("stocks :: ", this.stocks);
    }
}


// WEBPACK FOOTER //
// /Users/afsarahamed/Documents/symatis/projects/hyatt-angularweb/src/app/components/phase1/WhWorkOrder/AssignStocks/partywiseAssignStock.ts